/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase from './WireframeBase';
import tools from '../tools';
import wireframeCheckboxIcon from '../assets/icons/wireframeCheckboxIcon.svg';
import wireframeCheckbox from '../assets/icons/wireframeCheckbox.svg';
import wireframeEmptyCheckbox from '../assets/icons/wireframeEmptyCheckbox.svg';
import wireframeDash from '../assets/icons/wireframeDash.svg';
import {
  CHECKBOX_SIZE,
  Checkbox,
  type CheckboxProps,
} from '../canvasx/Checkbox';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { SPACER_2 } from '../utils/style';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import { Label } from '../components/wireframes/Label';

class WireframeCheckbox extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);
    // Backwards compat: uiStates used to be default, disabled
    if (!fabricObject.uiState || fabricObject.uiState === 'default') {
      fabricObject.set('uiState', 'enabled');
    }
  }

  tools() {
    return [
      [tools.ToolWireframeComponentState],
      [tools.ToolWireframeLabelPosition],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      this.showLabel ? [tools.ToolWireframeTextColor] : [],
    ];
  }

  static defaultHeight(): number {
    return CHECKBOX_SIZE;
  }

  static defaultWidth(): number {
    return CHECKBOX_SIZE;
  }

  static iconSvg() {
    return wireframeCheckboxIcon;
  }

  static externalName(): string {
    return 'Checkbox';
  }

  static componentStates() {
    return {
      empty: wireframeEmptyCheckbox,
      checkbox: wireframeCheckbox,
      dash: wireframeDash,
    };
  }

  componentStates() {
    return WireframeCheckbox.componentStates();
  }

  get componentStatesTitle() {
    return 'Checked state';
  }

  static labelPositions() {
    return ['left', 'right', 'noLabel'];
  }

  labelPositions(): string[] {
    return WireframeCheckbox.labelPositions();
  }

  get uiStates() {
    return ['enabled', 'disabled'];
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      scaleX,
      componentState = 'checkbox',
      uiState = 'enabled',
      textColor = 'black',
    } = this.fabricObject;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const opacity = uiState === 'disabled' ? 0.4 : 1;
    let renderedDimensions;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexRow
            verticalAlignment='center'
            gap={SPACER_2}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
          >
            {this.labelPosition() === 'left' && (
              <Label wireframe={this} color={textColor} opacity={opacity} />
            )}
            <Checkbox
              fill={{
                color: fill,
                opacity,
              }}
              type={componentState as CheckboxProps['type']}
            />
            {this.labelPosition() === 'right' && (
              <Label wireframe={this} color={textColor} opacity={opacity} />
            )}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeCheckbox;
